/**
 * Authentication Telemetry Utilities
 * Provides structured logging for authentication events
 * Integrated with Azure Application Insights for production monitoring
 * 
 * This is a recommendation from CODE_REVIEW_CONTEXT7.md (Section 10.2.2)
 */

export type AuthEventType =
  | 'login_attempt'
  | 'login_success'
  | 'login_failure'
  | 'logout'
  | 'token_refresh'
  | 'token_refresh_failure'
  | 'token_validation'
  | 'token_validation_failure'
  | 'silent_token_acquisition'
  | 'interaction_required'
  | 'api_call'
  | 'api_error'
  | 'redirect_attempt'
  | 'redirect_success'
  | 'redirect_processing'
  | 'token_acquisition_failure'
  | 'validation_failure';

export interface AuthEventData {
  event: AuthEventType;
  timestamp: string;
  userId?: string;
  email?: string;
  provider?: string;
  error?: string;
  metadata?: Record<string, any>;
}

/**
 * Authentication event logger
 */
class AuthTelemetry {
  private events: AuthEventData[] = [];
  private readonly maxEvents = 100;

  /**
   * Log an authentication event
   */
  logEvent(
    event: AuthEventType,
    data?: {
      userId?: string;
      email?: string;
      provider?: string;
      error?: string;
      metadata?: Record<string, any>;
    }
  ): void {
    const eventData: AuthEventData = {
      event,
      timestamp: new Date().toISOString(),
      ...data,
    };

    // Store event
    this.events.push(eventData);

    // Maintain max events limit
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      const emoji = this.getEventEmoji(event);
      const style = this.getEventStyle(event);
      console.log(
        `%c${emoji} [Auth] ${event}`,
        style,
        data || ''
      );
    }

    // Phase 7.2: Emit to Application Insights in production
    // Use fire-and-forget pattern to avoid blocking (async import)
    if (import.meta.env.PROD) {
      // Fire-and-forget: don't await to avoid blocking
      import('../services/applicationInsights.service')
        .then(({ trackEvent: trackAppInsightsEvent }) => {
          // Build properties object, filtering out undefined values
          const properties: Record<string, string> = {
            event,
            timestamp: eventData.timestamp,
          };
          
          // Add metadata, filtering out undefined values
          if (data?.metadata) {
            Object.entries(data.metadata).forEach(([key, val]) => {
              if (val !== undefined && val !== null) {
                properties[key] = String(val);
              }
            });
          }
          
          // Add optional fields only if they exist
          if (data?.userId) properties.userId = data.userId;
          if (data?.email) properties.email = data.email;
          if (data?.provider) properties.provider = data.provider;
          if (data?.error) properties.error = data.error;
          
          trackAppInsightsEvent(`auth.${event}`, properties);
        })
        .catch((error) => {
          // Silently fail if Application Insights is not available
          // This prevents telemetry failures from breaking the app
          if (import.meta.env.DEV) {
            console.debug('[AuthTelemetry] Application Insights not available:', error);
          }
        });
    }
  }

  /**
   * Track metric with dimensions
   * Phase 7.2: Mandatory metrics for redirect flow
   */
  trackMetric(
    metricName: string,
    value: number,
    dimensions?: Record<string, string | number>
  ): void {
    const metricData = {
      metricName,
      value,
      dimensions: dimensions || {},
      timestamp: new Date().toISOString(),
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.log(`[Auth Metrics] ${metricName}:`, metricData);
    }

    // Phase 7.2: Emit to Application Insights in production
    // Use fire-and-forget pattern to avoid blocking (async import)
    if (import.meta.env.PROD) {
      // Fire-and-forget: don't await to avoid blocking
      import('../services/applicationInsights.service')
        .then(({ trackMetric: trackAppInsightsMetric }) => {
          // Convert dimensions to properties (Application Insights expects string properties)
          const properties: Record<string, string> = {};
          if (dimensions) {
            Object.entries(dimensions).forEach(([key, val]) => {
              properties[key] = String(val);
            });
          }
          
          trackAppInsightsMetric(metricName, value, 1, undefined, undefined, properties);
        })
        .catch((error) => {
          // Silently fail if Application Insights is not available
          // This prevents telemetry failures from breaking the app
          if (import.meta.env.DEV) {
            console.debug('[AuthTelemetry] Application Insights not available:', error);
          }
        });
    }
  }

  /**
   * Get emoji for event type
   */
  private getEventEmoji(event: AuthEventType): string {
    const emojiMap: Record<AuthEventType, string> = {
      login_attempt: '🔐',
      login_success: '✅',
      login_failure: '❌',
      logout: '👋',
      token_refresh: '🔄',
      token_refresh_failure: '⚠️',
      token_validation: '🔍',
      token_validation_failure: '❌',
      silent_token_acquisition: '🔑',
      interaction_required: '👤',
      api_call: '📡',
      api_error: '🚨',
      redirect_attempt: '🔄',
      redirect_success: '✅',
      redirect_processing: '⏳',
      token_acquisition_failure: '🔑',
      validation_failure: '❌',
    };
    return emojiMap[event] || '📝';
  }

  /**
   * Get console style for event type
   */
  private getEventStyle(event: AuthEventType): string {
    const successEvents: AuthEventType[] = ['login_success', 'token_refresh', 'silent_token_acquisition'];
    const errorEvents: AuthEventType[] = ['login_failure', 'token_refresh_failure', 'token_validation_failure', 'api_error'];
    const warningEvents: AuthEventType[] = ['interaction_required'];

    if (successEvents.includes(event)) {
      return 'color: #10b981; font-weight: bold;';
    }
    if (errorEvents.includes(event)) {
      return 'color: #ef4444; font-weight: bold;';
    }
    if (warningEvents.includes(event)) {
      return 'color: #f59e0b; font-weight: bold;';
    }
    return 'color: #3b82f6; font-weight: bold;';
  }

  /**
   * Get recent events (for debugging)
   */
  getRecentEvents(count: number = 10): AuthEventData[] {
    return this.events.slice(-count);
  }

  /**
   * Get all events
   */
  getAllEvents(): AuthEventData[] {
    return [...this.events];
  }

  /**
   * Clear all events
   */
  clearEvents(): void {
    this.events = [];
  }

  /**
   * Export events as JSON (for debugging)
   */
  exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }
}

// Singleton instance
const authTelemetry = new AuthTelemetry();

/**
 * Log authentication event
 */
export function logAuthEvent(
  event: AuthEventType,
  data?: {
    userId?: string;
    email?: string;
    provider?: string;
    error?: string;
    metadata?: Record<string, any>;
  }
): void {
  authTelemetry.logEvent(event, data);
}

/**
 * Get recent auth events (for debugging)
 */
export function getRecentAuthEvents(count?: number): AuthEventData[] {
  return authTelemetry.getRecentEvents(count);
}

/**
 * Clear auth events
 */
export function clearAuthEvents(): void {
  authTelemetry.clearEvents();
}

/**
 * Export auth events as JSON
 */
export function exportAuthEvents(): string {
  return authTelemetry.exportEvents();
}

/**
 * Helper to log login attempt
 */
export function logLoginAttempt(provider: string = 'msal'): void {
  logAuthEvent('login_attempt', { provider });
}

/**
 * Helper to log login success
 */
export function logLoginSuccess(userId: string, email?: string, provider: string = 'msal'): void {
  logAuthEvent('login_success', { userId, email, provider });
}

/**
 * Helper to log login failure
 */
export function logLoginFailure(error: string, provider: string = 'msal'): void {
  logAuthEvent('login_failure', { error, provider });
}

/**
 * Helper to log logout
 */
export function logLogout(userId?: string): void {
  logAuthEvent('logout', { userId });
}

/**
 * Helper to log token refresh
 */
export function logTokenRefresh(success: boolean, error?: string): void {
  logAuthEvent(success ? 'token_refresh' : 'token_refresh_failure', { error });
}

/**
 * Helper to log API call
 */
export function logApiCall(endpoint: string, method: string = 'GET', metadata?: Record<string, any>): void {
  logAuthEvent('api_call', { 
    metadata: { 
      endpoint, 
      method,
      ...metadata 
    } 
  });
}

/**
 * Helper to log API error
 */
export function logApiError(endpoint: string, error: string, statusCode?: number): void {
  logAuthEvent('api_error', { 
    error,
    metadata: { 
      endpoint,
      statusCode 
    } 
  });
}

/**
 * Phase 7.2: Track redirect attempt (denominator for success rate)
 * Called when loginRedirect() is called successfully (initiation occurs)
 */
export function trackRedirectAttempt(idp?: string, flowType: string = 'signin'): void {
  authTelemetry.trackMetric('auth.redirect.attempt', 1, { idp: idp || 'none', flow_type: flowType });
  logAuthEvent('redirect_attempt', { provider: idp, metadata: { flowType } });
}

/**
 * Phase 7.2: Track redirect success (numerator for success rate)
 * Called after completeLogin() succeeds AND AuthContext.login() is called
 */
export function trackRedirectSuccess(
  idp?: string,
  flowType: string = 'signin',
  processingTimeMs?: number
): void {
  authTelemetry.trackMetric('auth.redirect.success', 1, { idp: idp || 'none', flow_type: flowType });
  logAuthEvent('redirect_success', {
    provider: idp,
    metadata: { flowType, processingTimeMs },
  });
}

/**
 * Phase 7.2: Track token acquisition failure
 */
export function trackTokenAcquisitionFailure(
  errorKind: string,
  idp?: string
): void {
  authTelemetry.trackMetric('auth.token.acquisition_failure', 1, {
    error_kind: errorKind,
    idp: idp || 'none',
  });
  logAuthEvent('token_acquisition_failure', {
    error: errorKind,
    provider: idp,
  });
}

/**
 * Phase 7.2: Track UserService validation failure
 */
export function trackValidationFailure(
  errorType: string,
  idp?: string
): void {
  authTelemetry.trackMetric('auth.validation.failure', 1, {
    error_type: errorType,
    idp: idp || 'none',
  });
  logAuthEvent('validation_failure', {
    error: errorType,
    provider: idp,
  });
}

/**
 * Phase 7.2 (MUST FIX #5): Track redirect processing time breakdown
 * Breaks down processing time into components
 */
export function trackRedirectProcessingTime(
  breakdown: {
    total: number;
    msal: number;
    token: number;
    validation: number;
    state_update: number;
  },
  outcome: 'success' | 'validation_failure' | 'token_failure',
  idp?: string
): void {
  // Emit individual component metrics
  authTelemetry.trackMetric('auth.redirect.processing_time_ms.total', breakdown.total, {
    outcome,
    idp: idp || 'none',
  });
  authTelemetry.trackMetric('auth.redirect.processing_time_ms.msal', breakdown.msal, {
    outcome,
    idp: idp || 'none',
  });
  authTelemetry.trackMetric('auth.redirect.processing_time_ms.token', breakdown.token, {
    outcome,
    idp: idp || 'none',
  });
  authTelemetry.trackMetric('auth.redirect.processing_time_ms.validation', breakdown.validation, {
    outcome,
    idp: idp || 'none',
  });
  authTelemetry.trackMetric('auth.redirect.processing_time_ms.state_update', breakdown.state_update, {
    outcome,
    idp: idp || 'none',
  });

  logAuthEvent('redirect_processing', {
    provider: idp,
    metadata: {
      processingTimeMs: breakdown.total,
      breakdown,
      outcome,
    },
  });
}

/**
 * Phase 7.2: Log structured auth event with correlation ID
 * All auth flow events must include correlation ID (from transaction ID or generated)
 */
export function logStructuredAuthEvent(
  event: AuthEventType,
  data: {
    correlationId?: string;
    flowType?: string;
    idp?: string;
    outcome?: 'success' | 'failure';
    errorKind?: string;
    errorType?: string;
    processingTimeMs?: number;
    [key: string]: any;
  }
): void {
  const structuredData = {
    correlationId: data.correlationId || `gen-${Date.now()}`,
    flowType: data.flowType || 'signin',
    idp: data.idp || 'none',
    timestamp: new Date().toISOString(),
    outcome: data.outcome,
    errorKind: data.errorKind,
    errorType: data.errorType,
    processingTimeMs: data.processingTimeMs,
    ...data,
  };

  logAuthEvent(event, {
    metadata: structuredData,
  });
}

// Export singleton for advanced usage
export { authTelemetry };
